import {useState} from "react";
import {useDispatch} from "react-redux";

import {clearPhoneVerificationTimerLS} from "@pg-mono/user";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {useMultileadTinderAbTest} from "../../ab_testing/hooks/use_multilead_tinder_ab_test";
import {openTinderMultiLeadModal} from "../../application/state/tinder_multi_lead_slice";
import {ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useSubscribeToNewsletter} from "../../newsletter/hooks/useSubscribeToNewsletter";
import {useAppSelector} from "../../store/hooks";
import {getUserSourceDisplay} from "../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {HomeMatchBasicPreferencesFlow} from "../../user/components/home_match_basic_preferences_flow/HomeMatchBasicPreferencesFlow";
import {UserPreferencesUpdateDataModal} from "../../user/components/preferences/UserPreferencesUpdateDataModal";
import {UserPhoneVerification} from "../../user/components/UserPhoneVerification";
import {UserUpdatePhoneNumberModal} from "../../user/components/UserUpdatePhoneNumberModal";
import {
    HomeMatchBasicPreferencesFlowCompletionReason,
    HomeMatchBasicPreferencesFlowInitialStep,
    HomeMatchBasicPreferencesFlowSource
} from "../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {closeFlow, nextStep, RegistrationFlowStep, setStep} from "../state/registration_flow_slice";
import {AuthModalVariant} from "./auth_modal/AuthModalHeaderVariants";
import {LeadAfterRegistration} from "./registration_flow/LeadAfterRegistration";
import {RegistrationMultilead} from "./registration_flow/RegistrationMultilead";
import {AuthModalRegistrationFlow} from "./AuthModalRegistrationFlow";

export const RegistrationFlow = () => {
    const dispatch = useDispatch();
    const flowState = useAppSelector((state) => state.registrationFlow);
    const {hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();
    const {saveNewsletterSearchData, subscribeToNotifications} = useSubscribeToNewsletter();
    const {data: userProfile} = useGetUserProfileQuery();
    const {isMultileadTinderEnabled} = useMultileadTinderAbTest();
    const postPriceRevealOverlayVariant = flowState.useLeadAfterRegistration ? "strong" : undefined;
    const homeMatchBasicEventCategory = flowState.gtmSourceSection || (flowState.userSource ? getUserSourceDisplay(flowState.userSource) : "");
    const [homeMatchInitials, setHomeMatchInitials] = useState<{
        source: HomeMatchBasicPreferencesFlowSource;
        initialStep: HomeMatchBasicPreferencesFlowInitialStep;
        consent: boolean;
    }>({
        source: "survey",
        initialStep: "survey",
        consent: false
    });
    const homeMatchBasicModalEventName = homeMatchInitials.source === "survey" ? "modal_post_sign_up" : "modal_post_lead";
    // Set when the price-reveal lead is sent; drives the multilead step shown after the preferences survey.
    // `wasOpened` marks that the MULTILEAD step was entered post-preferences, so its completion closes the flow.
    const [postLeadMultilead, setPostLeadMultilead] = useState<{refererApplicationUuid: string | null; wasOpened: boolean} | null>(null);

    if (!flowState.isActive) {
        return null;
    }

    const handlers = {
        onNextStep: () => {
            dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
        },
        onLeadConfirm: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentApplicationUuid: string | null = null) => {
            if (source === "afterLead") {
                setPostLeadMultilead({refererApplicationUuid: sentApplicationUuid, wasOpened: false});
            }
            setHomeMatchInitials({source, initialStep: isHomeMatchOn ? "survey" : "summary", consent: isHomeMatchOn});
            dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
        },
        onLeadClose: (isHomeMatchOn: boolean) => {
            setHomeMatchInitials({source: "survey", initialStep: isHomeMatchOn ? "survey" : "summary", consent: isHomeMatchOn});
            dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
        },
        onShowSimilar: () => {
            if (isMultileadTinderEnabled && flowState.propertyId && flowState.offerId && flowState.vendorId) {
                dispatch(closeFlow());
                dispatch(
                    openTinderMultiLeadModal({
                        originPropertyId: flowState.propertyId,
                        originOfferId: flowState.offerId,
                        originVendorId: flowState.vendorId,
                        refererApplicationUuid: null,
                        sourceSection: ApplicationSourceSection.POPUP_MODAL,
                        customSourceSection: "Multilead Tinder - post price reveal",
                        ctaText: InquiryButtonTypeName.SHOW_SIMILAR_OFFERS_NEARBY,
                        overlayVariant: postPriceRevealOverlayVariant
                    })
                );
                return;
            }

            dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
        },
        onCloseFlow: () => {
            if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && userProfile) {
                subscribeToNotifications(userProfile);
            }
            dispatch(closeFlow());
        }
    };

    // Multilead (classic or Tinder, same AB test) shown after the preferences step whenever the price-reveal lead was sent
    const tryOpenPostLeadMultilead = () => {
        if (!postLeadMultilead || postLeadMultilead.wasOpened) {
            return false;
        }

        if (!flowState.propertyId || !flowState.offerId || !flowState.vendorId) {
            return false;
        }

        if (isMultileadTinderEnabled) {
            dispatch(closeFlow());
            dispatch(
                openTinderMultiLeadModal({
                    originPropertyId: flowState.propertyId,
                    originOfferId: flowState.offerId,
                    originVendorId: flowState.vendorId,
                    refererApplicationUuid: postLeadMultilead.refererApplicationUuid,
                    sourceSection: ApplicationSourceSection.POPUP_MODAL,
                    customSourceSection: "Multilead Tinder - post price reveal lead",
                    overlayVariant: postPriceRevealOverlayVariant
                })
            );
            return true;
        }

        setPostLeadMultilead({...postLeadMultilead, wasOpened: true});
        dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
        return true;
    };

    const onPreferencesSuccess = (reason?: HomeMatchBasicPreferencesFlowCompletionReason) => {
        if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && flowState.preferences) {
            const {regions, rooms, price} = flowState.preferences;
            saveNewsletterSearchData({
                ...(regions?.length ? {search: {regions}} : {}),
                ...(rooms ? {rooms} : {}),
                ...(price ? {price: {upper: price}} : {})
            });
        }

        // After a sent lead the multilead replaces the follow-up steps (including the UPDATED_PREFERENCES confirmation modal)
        if (tryOpenPostLeadMultilead()) {
            return;
        }

        if (reason === "homeMatchSkipped") {
            handlers.onNextStep();
            return;
        }

        handlers.onCloseFlow();
    };

    return (
        <>
            {flowState.currentStep === RegistrationFlowStep.REGISTRATION && <AuthModalRegistrationFlow />}
            {flowState.currentStep === RegistrationFlowStep.PHONE_COMPLETION && (
                <UserUpdatePhoneNumberModal
                    isOpen
                    onSuccess={() => {
                        clearPhoneVerificationTimerLS();
                        handlers.onNextStep();
                    }}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.PHONE_VERIFICATION && <UserPhoneVerification isOpen onSuccess={handlers.onNextStep} />}
            {flowState.currentStep === RegistrationFlowStep.LEAD_AFTER_REGISTRATION && (
                <LeadAfterRegistration
                    isOpen
                    onConfirm={handlers.onLeadConfirm}
                    onClose={handlers.onCloseFlow}
                    onShowSimilar={handlers.onShowSimilar}
                    overlayVariant={postPriceRevealOverlayVariant}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.MULTILEAD && (
                <RegistrationMultilead
                    onComplete={postLeadMultilead?.wasOpened ? handlers.onCloseFlow : handlers.onLeadConfirm}
                    onClose={postLeadMultilead?.wasOpened ? handlers.onCloseFlow : handlers.onLeadClose}
                    overlayVariant={postPriceRevealOverlayVariant}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.PREFERENCES && (
                <HomeMatchBasicPreferencesFlow
                    isOpen
                    source={homeMatchInitials.source}
                    initialStep={homeMatchInitials.initialStep}
                    defaultHomeMatchBasicConsent={homeMatchInitials.consent}
                    propertyId={flowState.propertyId}
                    offerId={flowState.offerId}
                    overlayVariant={postPriceRevealOverlayVariant}
                    trackingContext={{
                        eventName: homeMatchBasicModalEventName,
                        eventCategory: homeMatchBasicEventCategory,
                        sourceModal: homeMatchBasicModalEventName
                    }}
                    amplitudeTrackingContext={
                        homeMatchInitials.source === "afterLead" &&
                        flowState.leadSource &&
                        flowState.offerId &&
                        flowState.propertyId &&
                        postLeadMultilead?.refererApplicationUuid
                            ? {
                                  source: flowState.leadSource,
                                  itemId: flowState.offerId,
                                  propertyId: flowState.propertyId,
                                  transactionId: postLeadMultilead.refererApplicationUuid
                              }
                            : undefined
                    }
                    onComplete={onPreferencesSuccess}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.UPDATED_PREFERENCES && (
                <UserPreferencesUpdateDataModal isOpen onClose={handlers.onCloseFlow} overlayVariant={postPriceRevealOverlayVariant} />
            )}
        </>
    );
};
import {useState} from "react";
import {useDispatch} from "react-redux";

import {clearPhoneVerificationTimerLS} from "@pg-mono/user";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {useMultileadTinderAbTest} from "../../ab_testing/hooks/use_multilead_tinder_ab_test";
import {openTinderMultiLeadModal} from "../../application/state/tinder_multi_lead_slice";
import {ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useSubscribeToNewsletter} from "../../newsletter/hooks/useSubscribeToNewsletter";
import {useAppSelector} from "../../store/hooks";
import {getUserSourceDisplay} from "../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {HomeMatchBasicPreferencesFlow} from "../../user/components/home_match_basic_preferences_flow/HomeMatchBasicPreferencesFlow";
import {UserPreferencesUpdateDataModal} from "../../user/components/preferences/UserPreferencesUpdateDataModal";
import {UserPhoneVerification} from "../../user/components/UserPhoneVerification";
import {UserUpdatePhoneNumberModal} from "../../user/components/UserUpdatePhoneNumberModal";
import {
    HomeMatchBasicPreferencesFlowCompletionReason,
    HomeMatchBasicPreferencesFlowInitialStep,
    HomeMatchBasicPreferencesFlowSource
} from "../../user/types/HomeMatchBasicPreferencesFlowTypes";
import {closeFlow, nextStep, RegistrationFlowStep, setStep} from "../state/registration_flow_slice";
import {AuthModalVariant} from "./auth_modal/AuthModalHeaderVariants";
import {LeadAfterRegistration} from "./registration_flow/LeadAfterRegistration";
import {RegistrationMultilead} from "./registration_flow/RegistrationMultilead";
import {AuthModalRegistrationFlow} from "./AuthModalRegistrationFlow";

export const RegistrationFlow = () => {
    const dispatch = useDispatch();
    const flowState = useAppSelector((state) => state.registrationFlow);
    const {hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();
    const {saveNewsletterSearchData, subscribeToNotifications} = useSubscribeToNewsletter();
    const {data: userProfile} = useGetUserProfileQuery();
    const {isMultileadTinderEnabled} = useMultileadTinderAbTest();
    const postPriceRevealOverlayVariant = flowState.useLeadAfterRegistration ? "strong" : undefined;
    const homeMatchBasicEventCategory = flowState.gtmSourceSection || (flowState.userSource ? getUserSourceDisplay(flowState.userSource) : "");
    const [homeMatchInitials, setHomeMatchInitials] = useState<{
        source: HomeMatchBasicPreferencesFlowSource;
        initialStep: HomeMatchBasicPreferencesFlowInitialStep;
        consent: boolean;
    }>({
        source: "survey",
        initialStep: "survey",
        consent: false
    });
    const homeMatchBasicModalEventName = homeMatchInitials.source === "survey" ? "modal_post_sign_up" : "modal_post_lead";
    // Set when the price-reveal lead is sent; drives the multilead step shown after the preferences survey.
    // `wasOpened` marks that the MULTILEAD step was entered post-preferences, so its completion closes the flow.
    const [postLeadMultilead, setPostLeadMultilead] = useState<{refererApplicationUuid: string | null; wasOpened: boolean} | null>(null);

    if (!flowState.isActive) {
        return null;
    }

    const handlers = {
        onNextStep: () => {
            dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
        },
        onLeadConfirm: (isHomeMatchOn: boolean, source: HomeMatchBasicPreferencesFlowSource, sentApplicationUuid: string | null = null) => {
            if (source === "afterLead") {
                setPostLeadMultilead({refererApplicationUuid: sentApplicationUuid, wasOpened: false});
            }
            setHomeMatchInitials({source, initialStep: isHomeMatchOn ? "survey" : "summary", consent: isHomeMatchOn});
            dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
        },
        onLeadClose: (isHomeMatchOn: boolean) => {
            setHomeMatchInitials({source: "survey", initialStep: isHomeMatchOn ? "survey" : "summary", consent: isHomeMatchOn});
            dispatch(nextStep({hasCompleteProfile, hasPhoneVerified}));
        },
        onShowSimilar: () => {
            if (isMultileadTinderEnabled && flowState.propertyId && flowState.offerId && flowState.vendorId) {
                dispatch(closeFlow());
                dispatch(
                    openTinderMultiLeadModal({
                        originPropertyId: flowState.propertyId,
                        originOfferId: flowState.offerId,
                        originVendorId: flowState.vendorId,
                        refererApplicationUuid: null,
                        sourceSection: ApplicationSourceSection.POPUP_MODAL,
                        customSourceSection: "Multilead Tinder - post price reveal",
                        ctaText: InquiryButtonTypeName.SHOW_SIMILAR_OFFERS_NEARBY,
                        overlayVariant: postPriceRevealOverlayVariant
                    })
                );
                return;
            }

            dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
        },
        onCloseFlow: () => {
            if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && userProfile) {
                subscribeToNotifications(userProfile);
            }
            dispatch(closeFlow());
        }
    };

    // Multilead (classic or Tinder, same AB test) shown after the preferences step whenever the price-reveal lead was sent
    const tryOpenPostLeadMultilead = () => {
        if (!postLeadMultilead || postLeadMultilead.wasOpened) {
            return false;
        }

        if (!flowState.propertyId || !flowState.offerId || !flowState.vendorId) {
            return false;
        }

        if (isMultileadTinderEnabled) {
            dispatch(closeFlow());
            dispatch(
                openTinderMultiLeadModal({
                    originPropertyId: flowState.propertyId,
                    originOfferId: flowState.offerId,
                    originVendorId: flowState.vendorId,
                    refererApplicationUuid: postLeadMultilead.refererApplicationUuid,
                    sourceSection: ApplicationSourceSection.POPUP_MODAL,
                    customSourceSection: "Multilead Tinder - post price reveal lead",
                    overlayVariant: postPriceRevealOverlayVariant
                })
            );
            return true;
        }

        setPostLeadMultilead({...postLeadMultilead, wasOpened: true});
        dispatch(setStep({step: RegistrationFlowStep.MULTILEAD}));
        return true;
    };

    const onPreferencesSuccess = (reason?: HomeMatchBasicPreferencesFlowCompletionReason) => {
        if (flowState.titleVariant === AuthModalVariant.NOTIFICATION_SUBSCRIPTION && flowState.preferences) {
            const {regions, rooms, price} = flowState.preferences;
            saveNewsletterSearchData({
                ...(regions?.length ? {search: {regions}} : {}),
                ...(rooms ? {rooms} : {}),
                ...(price ? {price: {upper: price}} : {})
            });
        }

        // After a sent lead the multilead replaces the follow-up steps (including the UPDATED_PREFERENCES confirmation modal)
        if (tryOpenPostLeadMultilead()) {
            return;
        }

        if (reason === "homeMatchSkipped") {
            handlers.onNextStep();
            return;
        }

        handlers.onCloseFlow();
    };

    return (
        <>
            {flowState.currentStep === RegistrationFlowStep.REGISTRATION && <AuthModalRegistrationFlow />}
            {flowState.currentStep === RegistrationFlowStep.PHONE_COMPLETION && (
                <UserUpdatePhoneNumberModal
                    isOpen
                    onSuccess={() => {
                        clearPhoneVerificationTimerLS();
                        handlers.onNextStep();
                    }}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.PHONE_VERIFICATION && <UserPhoneVerification isOpen onSuccess={handlers.onNextStep} />}
            {flowState.currentStep === RegistrationFlowStep.LEAD_AFTER_REGISTRATION && (
                <LeadAfterRegistration
                    isOpen
                    onConfirm={handlers.onLeadConfirm}
                    onClose={handlers.onCloseFlow}
                    onShowSimilar={handlers.onShowSimilar}
                    overlayVariant={postPriceRevealOverlayVariant}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.MULTILEAD && (
                <RegistrationMultilead
                    onComplete={postLeadMultilead?.wasOpened ? handlers.onCloseFlow : handlers.onLeadConfirm}
                    onClose={postLeadMultilead?.wasOpened ? handlers.onCloseFlow : handlers.onLeadClose}
                    overlayVariant={postPriceRevealOverlayVariant}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.PREFERENCES && (
                <HomeMatchBasicPreferencesFlow
                    isOpen
                    source={homeMatchInitials.source}
                    initialStep={homeMatchInitials.initialStep}
                    defaultHomeMatchBasicConsent={homeMatchInitials.consent}
                    propertyId={flowState.propertyId}
                    offerId={flowState.offerId}
                    overlayVariant={postPriceRevealOverlayVariant}
                    trackingContext={{
                        eventName: homeMatchBasicModalEventName,
                        eventCategory: homeMatchBasicEventCategory,
                        sourceModal: homeMatchBasicModalEventName
                    }}
                    amplitudeTrackingContext={
                        homeMatchInitials.source === "afterLead" &&
                        flowState.leadSource &&
                        flowState.offerId &&
                        flowState.propertyId &&
                        postLeadMultilead?.refererApplicationUuid
                            ? {
                                  source: flowState.leadSource,
                                  itemId: flowState.offerId,
                                  propertyId: flowState.propertyId,
                                  transactionId: postLeadMultilead.refererApplicationUuid
                              }
                            : undefined
                    }
                    onComplete={onPreferencesSuccess}
                />
            )}
            {flowState.currentStep === RegistrationFlowStep.UPDATED_PREFERENCES && (
                <UserPreferencesUpdateDataModal isOpen onClose={handlers.onCloseFlow} overlayVariant={postPriceRevealOverlayVariant} />
            )}
        </>
    );
};
